import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  NgIterable,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { forkJoin } from 'rxjs';
import { ComplianceDisplayCardBase } from 'src/app/classes/base-classes/compliance-display-card-base';
import { InsuranceComplianceResultToLabelMap } from 'src/app/classes/insurance-compliance-result-to-label-map';
import {
  CanadianWorkersCompensation,
  Company,
  CompanyVendor,
  InsurancePolicy,
  Vendor
} from 'src/app/classes/models';
import { ComplianceSetting } from 'src/app/classes/models/compliance-setting';
import { AUTOInsuranceResultField } from 'src/app/interfaces/auto-insurance-result-field';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GLInsuranceResultField } from 'src/app/interfaces/gl-insurance-result-field';
import { PLInsuranceResultField } from 'src/app/interfaces/pl-insurance-result-field';
import { UMBInsuranceResultField } from 'src/app/interfaces/umb-insurance-result-field';
import { WCInsuranceResultField } from 'src/app/interfaces/wc-insurance-result-field';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ComplianceService } from 'src/app/services/compliance.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-insurance-info-display-card',
  templateUrl: './insurance-info-display-card.component.html',
  styleUrls: ['./insurance-info-display-card.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class InsuranceInfoDisplayCardComponent
  extends ComplianceDisplayCardBase
  implements OnInit, OnChanges
{
  constructor(
    private CompanyVendorService: CompanyVendorService,
    private ComplianceService: ComplianceService,
    private ToastService: ToastService,
    private CurrencyPipe: CurrencyPipe
  ) {
    super();
  }

  @Input() Vendor!: Vendor;
  @Input() Company!: Company;
  @Input() CompanyVendor!: CompanyVendor;
  @Input() ComplianceResults!: any;
  ComplianceSetting!: ComplianceSetting;
  CanadianWorkersCompensationFindingAndResults: {
    name: string;
    finding: string | null;
    result: string | null;
  }[] = [];

  PolicyTypeToPolicyResults: {
    GL: GLInsuranceResultField[];
    AUTO: AUTOInsuranceResultField[];
    UMB: UMBInsuranceResultField[];
    WC: WCInsuranceResultField[];
    PL: PLInsuranceResultField[];
  } = {
    GL: [
      'generalLiability_amBestRating',
      'generalLiability_financialSizeCategory',
      'generalLiability_policyAmountPerOccurance',
      'generalLiability_policyAmountAggregate',
      'generalLiability_productOps',
      'generalLiability_medicalExpenses',
      'generalLiability_damageToRentedPremises',
      'generalLiability_nonOwnedAutoAmount',
      'personalAdvertisementInjury',
      'glIsNamed',
      'glIsAgency',
      'glHiredAutos',
      'glNonOwnedAutos',
      'generalLiability_hasAiOngoingOps',
      'generalLiability_hasAiCompOps',
      'generalLiability_hasPnc',
      'generalLiability_hasPolicyDoc',
      'generalLiability_hasDescOps',
      'generalLiability_hasRequiredForms',
      'generalLiability_isContractualLiability',
      'generalLiability_isPerOccurrence',
      'glPerProjLocAgg',
      'glAdditionalInsuredEndorsements',
      'generalLiability_InsNameFailed',
      'generalLiability_legalNameMismatch'
    ],
    AUTO: [
      'allowNonCommercialPolicy',
      'autoLiability_amBestRating',
      'autoLiability_financialSizeCategory',
      'autoLiability_combinedSingleLimit',
      'autoLiability_propertyDamageLimit',
      'autoLiability_bodilyInjuryPerPerson',
      'autoLiability_bodilyInjuryPerAccident',
      'autoIsNamed',
      'autoIsAgency',
      'autoHasNonOwnedAuto',
      'autoLiability_hasPolicyDoc',
      'autoLiability_hasDescOps',
      'autoLiability_hasRequiredForms',
      'autoAdditionalInsuredEndorsements',
      'autoLiability_InsNameFailed',
      'autoLiability_legalNameMismatch'
    ],
    UMB: [
      'umbrellaLiability_amBestRating',
      'umbrellaLiability_financialSizeCategory',
      'umbrellaLiability_policyAmountPerOccurance',
      'umbrellaLiability_policyAmountAggregate',
      'umbIsAgency',
      'umbIsNamed',
      'umbrellaLiability_hasPolicyDoc',
      'umbrellaLiability_hasDescOps',
      'umbrellaLiability_hasRequiredForms',
      'umbPrimaryNonContributory',
      'umbOccurrenceBased',
      'umbrellaLiability_InsNameFailed',
      'umbrellaLiability_legalNameMismatch'
    ],
    WC: [
      'workersCompensation_amBestRating',
      'workersCompensation_financialSizeCategory',
      'workersCompensation_eachIncidentEmployersLimit',
      'workersCompensation_eachEmployeeMinimum',
      'workersCompensation_eachEmployerMinimum',
      'wkIsAgency',
      'workersCompensation_hasDescOps',
      'workersCompensation_hasRequiredForms',
      'workersCompensation_officerExcluded',
      'workersCompensation_InsNameFailed',
      'workersCompensation_legalNameMismatch'
    ],
    PL: [
      'errorsAndOmissions_amBestRating',
      'errorsAndOmissions_financialSizeCategory',
      'errorsAndOmissions_policyAmount',
      'errIsNamed',
      'errIsAgency',
      'errorsAndOmissions_hasDescOps',
      'professionalLiability_InsNameFailed',
      'professionalLiability_legalNameMismatch'
    ]
  };

  PolicyTypes: ['GL', 'AUTO', 'UMB', 'WC', 'PL'] = [
    'GL',
    'AUTO',
    'UMB',
    'WC',
    'PL'
  ];

  ComplianceResultToLabelMap = new InsuranceComplianceResultToLabelMap();

  // Insurance certificates cannot contain multiple insurance policies of the same type.
  PolicyTypeToInsurancePolicyMap: {
    GL?: InsurancePolicy;
    AUTO?: InsurancePolicy;
    UMB?: InsurancePolicy;
    WC?: InsurancePolicy;
    PL?: InsurancePolicy;
  } = {
    GL: undefined,
    AUTO: undefined,
    UMB: undefined,
    WC: undefined,
    PL: undefined
  };

  PolicyTypeToPolicyLabelMap: {
    GL: 'General Liability';
    AUTO: 'Automotive Liability';
    UMB: 'Umbrella Liability';
    WC: "Worker's Compensation";
    PL: 'Professional Liability';
  } = {
    GL: 'General Liability',
    AUTO: 'Automotive Liability',
    UMB: 'Umbrella Liability',
    WC: "Worker's Compensation",
    PL: 'Professional Liability'
  };

  PolicyTypeToPolicyRequirementSetting: {
    GL: 'generalLiability_isRequired';
    AUTO: 'autoLiability_isRequired';
    UMB: 'umbrellaLiability_isRequired';
    WC: 'workersCompensation_isRequired';
    PL: 'errorsAndOmissions_isRequired';
  } = {
    GL: 'generalLiability_isRequired',
    AUTO: 'autoLiability_isRequired',
    UMB: 'umbrellaLiability_isRequired',
    WC: 'workersCompensation_isRequired',
    PL: 'errorsAndOmissions_isRequired'
  };

  get coverageTypeLabel(): string {
    return (
      this.CompanyVendor?.WCB?.type || this.CompanyVendor?.WSIB?.type || ''
    );
  }

  getPolicySettingValue(PolicyResult: string) {
    const settingValue =
      this.ComplianceSetting[
        this.ComplianceResultToLabelMap[
          PolicyResult as keyof typeof this.ComplianceResultToLabelMap
        ].setting
      ];

    if (
      new Set([
        'generalLiability_legalNameMismatch',
        'autoLiability_legalNameMismatch',
        'umbrellaLiability_legalNameMismatch',
        'workersCompensation_legalNameMismatch',
        'professionalLiability_legalNameMismatch',
        'allowNonCommercialPolicy'
      ]).has(PolicyResult) &&
      typeof settingValue === 'boolean'
    ) {
      return settingValue ? 'No' : 'Yes';
    }

    if (PolicyResult === 'workersCompensation_officerExcluded') {
      return settingValue ? 'ALLOWED' : 'NOT ALLOWED';
    }

    if (typeof settingValue === 'boolean') {
      return settingValue ? 'Yes' : 'No';
    }

    if (typeof settingValue === 'number') {
      return this.CurrencyPipe.transform(
        settingValue,
        'USD',
        'symbol',
        '1.0-0'
      );
    }

    return settingValue;
  }

  getPolicyTypeResult(PolicyType: 'GL' | 'AUTO' | 'UMB' | 'WC' | 'PL') {
    return this.PolicyTypeToPolicyResults[PolicyType] as NgIterable<
      | GLInsuranceResultField
      | AUTOInsuranceResultField
      | UMBInsuranceResultField
      | WCInsuranceResultField
      | PLInsuranceResultField
    >;
  }

  processClientAndVendorDataForCompliance() {
    if (!this.Vendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Vendor information.',
        { returnError: true }
      );
    }

    if (!this.Company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client information.',
        { returnError: true }
      );
    }

    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client/Vendor information.',
        { returnError: true }
      );
    }

    if (!this.ComplianceResults) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing compliance results.',
        { returnError: true }
      );
    }

    const { Vendor, Company, InsuranceCertificates, WSIB, WCB } =
      this.CompanyVendor;

    Object.assign(this, { Vendor, Company });

    const { InsuranceAgents } = this.Vendor;
    const { ComplianceSettings, country: clientCountry } = this.Company;

    if (!clientCountry) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client country information.',
        { returnError: true }
      );
    }

    if (clientCountry !== 'CA') {
      const ResultsToOmit = ['glHiredAutos', 'glNonOwnedAutos'];
      ResultsToOmit.forEach((result) => {
        delete this.ComplianceResults[result];
      });
    }

    if (!ComplianceSettings || ComplianceSettings.length !== 1) {
      // Missing / ambiguous settings must not crash My Client — license and
      // progress UI still need to render (e.g. No Licensed Trades QA fixtures).
      this.ToastService.addErrorToast(
        'Error',
        'Could not retrieve compliance setting.'
      );
      return;
    }

    this.ComplianceSetting = ComplianceSettings[0];

    const CanadianWorkersCompensations: CanadianWorkersCompensation[] = [];

    if (WCB) {
      CanadianWorkersCompensations.push(WCB);
    }

    if (WSIB) {
      CanadianWorkersCompensations.push(WSIB);
    }

    if (clientCountry === 'CA') {
      if (
        CanadianWorkersCompensations.length > 0 &&
        ComplianceSettings &&
        ComplianceSettings.length === 1
      ) {
        const WorkersCompRequired =
          !!ComplianceSettings[0].workersCompensation_isRequired;

        this.getCanadianWorkersCompResults(
          CanadianWorkersCompensations,
          WorkersCompRequired
        );
      }
    }

    if (InsuranceCertificates) {
      InsuranceCertificates.forEach((InsuranceCertificate) => {
        const { InsurancePolicies } = InsuranceCertificate;

        if (!InsurancePolicies) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Insurance certificate is missing insurance policies.',
            { returnError: true }
          );
        }

        InsurancePolicies.forEach((InsurancePolicy) => {
          const { policyType } = InsurancePolicy;

          if (!policyType) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Insurance policy is missing policy type.',
              { returnError: true }
            );
          }

          Object.assign(this.PolicyTypeToInsurancePolicyMap, {
            [policyType]: InsurancePolicy
          });
        });
      });
    }
  }

  getFormattedFindingValue(value: any) {
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    } else if (typeof value === 'number') {
      return this.CurrencyPipe.transform(value, 'USD', 'symbol', '1.0-0');
    } else {
      return value;
    }
  }

  getCanadianWorkersCompResults(
    CanadianWorkersCompensations: CanadianWorkersCompensation[],
    WorkerCompRequired: boolean
  ) {
    this.CanadianWorkersCompensationFindingAndResults.splice(0);
    CanadianWorkersCompensations.forEach((CWC) => {
      this.CanadianWorkersCompensationFindingAndResults.push({
        name: CWC.type || '',
        ...this.ComplianceService.calculateCanadianCompensationFindingAndResult(
          CWC,
          WorkerCompRequired
        )
      });
    });
  }

  ngOnInit(): void {
    if (this.loadDataRemotely) {
      if (!this.VendorId) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot load data remotely. Missing Vendor Id.',
          { returnError: true }
        );
      }

      if (!this.CompanyId) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot load data remotely. Missing Client Id.',
          { returnError: true }
        );
      }

      this.ComplianceService.getClientVendorSharedStates(
        this.CompanyId,
        this.VendorId
      ).subscribe({
        next: (ClientVendorSharedStates) => {
          const ClientVendorSharedStatesSet = new Set(ClientVendorSharedStates);

          const CompanyVendorQuery: GenericObject = {
            CompanyId: this.CompanyId,
            VendorId: this.VendorId,
            attributes: JSON.stringify(['id', 'country']),
            Vendor: JSON.stringify({
              options: { required: true },
              attributes: ['id'],
              aliasedAttributes: [
                'hasProfessionalTrade',
                'hasNonProfessionalTrade',
                'hasProfessionalOrNonProfessionalTrade'
              ],
              InsuranceAgent: {}
            }),
            Company: JSON.stringify({
              attributes: ['id', 'legalName', 'hasGlobalCompliance', 'country'],
              ComplianceSetting: {
                options: { required: false },
                CompanyComplianceTypeId: {
                  $col: 'CompanyVendor.CompanyComplianceTypeId'
                },
                literals: {
                  id: { $eq: 'selectComplianceSettingIdForVendorRiskLevel' }
                }
              }
            }),
            InsuranceCertificate: {
              CompanyId: this.CompanyId,
              status: { $notIn: ['Archived', 'Replaced'] },
              aliasedAttributes: ['nextExpiringInsurancePolicyDate'],
              InsurancePolicy: {
                options: { required: true }
              }
            }
          };

          if (ClientVendorSharedStatesSet.has('ON')) {
            CompanyVendorQuery['WSIB'] = {};
          }

          if (ClientVendorSharedStatesSet.has('AB')) {
            CompanyVendorQuery['WCB'] = {};
          }

          forkJoin([
            this.CompanyVendorService.getCompanyVendor(CompanyVendorQuery),
            this.ComplianceService.getComplianceResults(
              this.CompanyId!,
              this.VendorId!
            )
          ]).subscribe({
            next: ([CompanyVendor, ComplianceResults]) => {
              if (!CompanyVendor) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Missing Client/Vendor information.',
                  { returnError: true }
                );
              }

              const { Vendor, Company } = CompanyVendor;

              if (!Vendor) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Missing Vendor information.',
                  { returnError: true }
                );
              }

              if (!Company) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Missing Client information.',
                  { returnError: true }
                );
              }

              Object.assign(this, {
                Company,
                Vendor,
                CompanyVendor,
                ComplianceResults
              });

              this.processClientAndVendorDataForCompliance();
            }
          });
        }
      });
    } else {
      this.processClientAndVendorDataForCompliance();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { CompanyVendor } = changes;

    if (CompanyVendor) {
      this.processClientAndVendorDataForCompliance();
    }
  }
}
